import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ShieldCheck,
  KeyRound,
  Clock,
  Copy,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ListVideo,
  ArrowRight,
  Loader2,
  ExternalLink,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function isValidMac(raw) {
  const stripped = (raw || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  return /^[0-9A-F]{12}$/.test(stripped);
}

// Live-format a MAC input as the user types.
// Accepts anything (colons, dashes, lowercase) and returns AA:BB:CC:DD:EE:FF
// truncated to at most 6 hex pairs.
function formatMac(raw) {
  const hex = (raw || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase().slice(0, 12);
  return hex.match(/.{1,2}/g)?.join(":") || "";
}

// Build Xtream Codes-style URLs from host/port/username/password.
// Mirrors admin panel behaviour in `Playlists.jsx`.
function buildXtreamUrls({ host, port, username, password }) {
  let h = (host || "").trim().replace(/\/+$/, "");
  if (!h) return { m3u_url: "", epg_url: "" };
  if (!/^https?:\/\//i.test(h)) h = `http://${h}`;
  const hasPortInHost = /:\d+$/.test(h.replace(/^https?:\/\//i, ""));
  const portStr = String(port || "").trim();
  if (portStr && !hasPortInHost) h = `${h}:${portStr}`;
  const u = encodeURIComponent(username || "");
  const p = encodeURIComponent(password || "");
  return {
    m3u_url: `${h}/get.php?username=${u}&password=${p}&type=m3u_plus&output=m3u8`,
    epg_url: `${h}/xmltv.php?username=${u}&password=${p}`,
  };
}

export default function Trial() {
  const [mac, setMac] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Add-playlist dialog state (opens after trial success on button click)
  const [addOpen, setAddOpen] = useState(false);
  const [addSource, setAddSource] = useState("xtream"); // "xtream" | "raw"
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState(null);
  const [addForm, setAddForm] = useState({
    name: "",
    host: "",
    port: "",
    username: "",
    password: "",
    m3u_url: "",
    epg_url: "",
  });

  const resetAddForm = () => {
    setAddForm({
      name: "",
      host: "",
      port: "",
      username: "",
      password: "",
      m3u_url: "",
      epg_url: "",
    });
    setAddError("");
    setAddSuccess(null);
    setAddSource("xtream");
  };

  const openAddPlaylist = () => {
    resetAddForm();
    setAddOpen(true);
  };

  const submitAddPlaylist = async (e) => {
    e.preventDefault();
    if (!result?.activation_key) return;
    setAddError("");
    setAddSuccess(null);

    const name = addForm.name.trim();
    if (!name) {
      setAddError("Please give your playlist a name.");
      return;
    }
    let payload = { activation_key: result.activation_key, name };
    if (addSource === "xtream") {
      if (!addForm.host.trim() || !addForm.username.trim()) {
        setAddError("Host and username are required for Xtream Codes.");
        return;
      }
      const built = buildXtreamUrls(addForm);
      if (!built.m3u_url) {
        setAddError("Could not build the Xtream URL — please check the host.");
        return;
      }
      payload.m3u_url = built.m3u_url;
      payload.epg_url = built.epg_url || null;
    } else {
      if (!addForm.m3u_url.trim()) {
        setAddError("Paste your M3U URL.");
        return;
      }
      payload.m3u_url = addForm.m3u_url.trim();
      payload.epg_url = addForm.epg_url.trim() || null;
    }

    setAddBusy(true);
    try {
      const res = await fetch(`${BACKEND}/api/trial/add-playlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setAddError(data?.detail || "Could not attach the playlist.");
        return;
      }
      setAddSuccess(data);
    } catch (err) {
      setAddError(err?.message || "Network error. Try again.");
    } finally {
      setAddBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidMac(mac)) {
      setError("Please enter a valid MAC address (12 hex characters).");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/trial/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mac_address: mac }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data.detail || `Request failed (${r.status})`);
        return;
      }
      setResult(data);
    } catch (err) {
      setError(err?.message || "Network error");
    } finally {
      setBusy(false);
    }
  };

  const copyKey = async () => {
    if (!result?.activation_key) return;
    try {
      await navigator.clipboard.writeText(result.activation_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid="trial-page">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="trial-home-link">
            <img src="/za-logo-icon.png" alt="ZA Player Pro" className="h-9 w-9 rounded-lg" />
            <span className="text-lg font-bold tracking-tight">ZA Player Pro</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-emerald-400"
            data-testid="trial-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="relative overflow-hidden py-24 md:py-32">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-16 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
        </div>

        <div className="mx-auto grid max-w-4xl gap-12 px-6 lg:grid-cols-2 lg:gap-16">
          {/* Left: pitch */}
          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
              Free trial
            </div>
            <h1 className="text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl" data-testid="trial-title">
              Get 5&nbsp;days on us.
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-neutral-300">
              Enter the MAC address of the device you&apos;d like to try ZA Player Pro on.
              We&apos;ll instantly issue a free 5-day activation key — no email, no
              account, no strings.
            </p>

            <ul className="mt-8 space-y-4 text-sm text-neutral-300" data-testid="trial-benefits">
              <li className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-emerald-400" />
                <span>One trial per MAC address, ever. No abuse, no drama.</span>
              </li>
              <li className="flex items-start gap-3">
                <KeyRound className="mt-0.5 h-5 w-5 flex-none text-emerald-400" />
                <span>Bring your own M3U or Xtream Codes playlist — we&apos;re a player only.</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 flex-none text-emerald-400" />
                <span>After 5 days the key stops working. No hidden charges.</span>
              </li>
            </ul>

            <div className="mt-8 rounded-2xl border border-neutral-800 bg-neutral-950/60 p-4 text-xs text-neutral-400">
              <span className="font-semibold text-neutral-200">Where do I find my MAC?</span><br />
              On the activation screen of ZA Player Pro on your device — the app shows
              your MAC in the header of the activation panel. Copy it here.
            </div>
          </div>

          {/* Right: form / result card */}
          <div className="relative">
            <div className="rounded-3xl border border-white/10 bg-neutral-950/70 p-8 shadow-[0_40px_120px_-10px_rgba(74,222,128,0.15)] backdrop-blur">
              {!result ? (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="trial-form">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-neutral-400">
                      Device MAC address
                    </label>
                    <Input
                      value={mac}
                      onChange={(e) => setMac(formatMac(e.target.value))}
                      placeholder="AA:BB:CC:11:22:33"
                      className="border-white/10 bg-black text-lg tracking-widest text-white placeholder:text-neutral-600 focus-visible:ring-emerald-500"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      inputMode="text"
                      maxLength={17}
                      data-testid="trial-mac-input"
                    />
                    <p className="mt-2 text-[11px] text-neutral-500">
                      Auto-formatted with colons. Case-insensitive.
                    </p>
                  </div>

                  {error && (
                    <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200" data-testid="trial-error">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={busy || !mac.trim()}
                    className="w-full rounded-full bg-emerald-500 py-6 text-base font-semibold text-black hover:bg-emerald-400 disabled:opacity-50"
                    data-testid="trial-submit-btn"
                  >
                    {busy ? "Claiming trial…" : "Claim my 5-day trial"}
                  </Button>

                  <p className="text-center text-[11px] text-neutral-500">
                    By continuing you agree to our{" "}
                    <a href="/terms" className="underline hover:text-emerald-400">Terms</a>
                    {" "}and{" "}
                    <a href="/privacy" className="underline hover:text-emerald-400">Privacy Policy</a>.
                  </p>
                </form>
              ) : (
                <div className="space-y-6 text-center" data-testid="trial-result">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
                    <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold" data-testid="trial-success-title">
                      Your trial is ready
                    </h2>
                    <p className="mt-1 text-sm text-neutral-400">
                      Valid for {result.days_remaining} days. Enter this key in ZA Player Pro on your device.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={copyKey}
                    className="group w-full rounded-2xl border border-emerald-400/30 bg-black/60 py-5 text-center transition-all hover:bg-black"
                    data-testid="trial-key-display"
                  >
                    <div className="mb-1 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      Activation key {copied ? "— copied!" : "— tap to copy"}
                    </div>
                    <div className="flex items-center justify-center gap-3 font-mono text-2xl font-bold tracking-[0.4em] text-emerald-400">
                      {result.activation_key}
                      <Copy className="h-4 w-4 text-neutral-500 transition-colors group-hover:text-emerald-400" />
                    </div>
                  </button>

                  <div className="text-xs text-neutral-500">
                    Expires:{" "}
                    <span className="text-neutral-300">
                      {new Date(result.expires_at).toLocaleString()}
                    </span>
                  </div>

                  {/* Next step — attach a playlist to this activation
                      key right here on the site, exactly like the admin
                      panel does. Opens an in-page dialog. */}
                  <Button
                    onClick={openAddPlaylist}
                    className="w-full rounded-full bg-emerald-500 py-6 text-base font-semibold text-black hover:bg-emerald-400"
                    data-testid="trial-add-playlist-btn"
                  >
                    <ListVideo className="mr-2 h-5 w-5" />
                    Add Playlist
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="-mt-3 text-[11px] text-neutral-500">
                    Enter your Xtream Codes credentials (or paste a raw M3U URL) — we&apos;ll
                    attach the playlist to your activation key automatically.
                  </p>

                  <Link to="/">
                    <Button
                      variant="outline"
                      className="rounded-full border-white/20 bg-white/5 px-6 text-white hover:bg-white/10"
                      data-testid="trial-done-btn"
                    >
                      Back to home
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} ZA Player Pro. Player-only application. No content included.
      </footer>

      {/* Add Playlist dialog — mirrors the admin panel Create Playlist form
          and auto-attaches the new playlist to the caller's activation key. */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetAddForm(); }}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-neutral-900 text-white sm:max-w-lg" data-testid="add-playlist-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListVideo className="h-5 w-5 text-emerald-400" />
              Add Playlist
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              This playlist will be attached to activation key{" "}
              <span className="font-mono text-emerald-400">
                {result?.activation_key}
              </span>
              . Trials are limited to one playlist.
            </DialogDescription>
          </DialogHeader>

          {addSuccess ? (
            <div className="space-y-4 py-2" data-testid="add-playlist-success">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-400" />
                  <div>
                    <div className="font-semibold">
                      Playlist &quot;{addSuccess.playlist_name}&quot; linked.
                    </div>
                    <p className="mt-1 text-xs text-emerald-100/80">
                      Install ZA Player Pro on any device, enter your activation
                      key, and this playlist will load automatically. No further
                      setup needed.
                    </p>
                  </div>
                </div>
              </div>
              <Link to="/#downloads" className="block">
                <Button
                  className="w-full rounded-full bg-emerald-500 py-6 text-base font-semibold text-black hover:bg-emerald-400"
                  data-testid="add-playlist-download-app"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Download ZA Player Pro
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submitAddPlaylist} className="space-y-4 py-2" data-testid="add-playlist-form">
              <div>
                <Label className="text-xs text-neutral-400">Playlist name</Label>
                <Input
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="My provider"
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                  maxLength={80}
                  data-testid="add-playlist-name"
                />
              </div>

              <Tabs value={addSource} onValueChange={setAddSource}>
                <TabsList className="grid w-full grid-cols-2 bg-neutral-950">
                  <TabsTrigger value="xtream" data-testid="add-playlist-tab-xtream">
                    Xtream Codes
                  </TabsTrigger>
                  <TabsTrigger value="raw" data-testid="add-playlist-tab-raw">
                    Raw M3U URL
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="xtream" className="mt-4 space-y-3">
                  <div>
                    <Label className="text-xs text-neutral-400">Host</Label>
                    <Input
                      value={addForm.host}
                      onChange={(e) => setAddForm({ ...addForm, host: e.target.value })}
                      placeholder="line.myprovider.com"
                      className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                      autoCapitalize="off"
                      autoCorrect="off"
                      data-testid="add-playlist-host"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <Label className="text-xs text-neutral-400">Port</Label>
                      <Input
                        value={addForm.port}
                        onChange={(e) => setAddForm({ ...addForm, port: e.target.value })}
                        placeholder="80"
                        className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                        inputMode="numeric"
                        data-testid="add-playlist-port"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-neutral-400">Username</Label>
                      <Input
                        value={addForm.username}
                        onChange={(e) => setAddForm({ ...addForm, username: e.target.value })}
                        placeholder="your-username"
                        className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                        autoCapitalize="off"
                        autoCorrect="off"
                        data-testid="add-playlist-username"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-400">Password</Label>
                    <Input
                      type="password"
                      value={addForm.password}
                      onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                      placeholder="••••••••"
                      className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-600"
                      data-testid="add-playlist-password"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="raw" className="mt-4 space-y-3">
                  <div>
                    <Label className="text-xs text-neutral-400">M3U URL</Label>
                    <Input
                      value={addForm.m3u_url}
                      onChange={(e) => setAddForm({ ...addForm, m3u_url: e.target.value })}
                      placeholder="http://host/get.php?username=…&password=…&type=m3u_plus"
                      className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-600"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      data-testid="add-playlist-m3u-url"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-400">
                      EPG URL <span className="text-neutral-600">(optional)</span>
                    </Label>
                    <Input
                      value={addForm.epg_url}
                      onChange={(e) => setAddForm({ ...addForm, epg_url: e.target.value })}
                      placeholder="http://host/xmltv.php?username=…&password=…"
                      className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-600"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      data-testid="add-playlist-epg-url"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              {addError && (
                <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-100" data-testid="add-playlist-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-red-400" />
                  <span>{addError}</span>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAddOpen(false)}
                  className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                  data-testid="add-playlist-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={addBusy}
                  className="bg-emerald-500 text-black hover:bg-emerald-400"
                  data-testid="add-playlist-submit"
                >
                  {addBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Linking…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Link to my key
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
